import { formConfigBase } from '@app/shared/constants/form-config-base';
import {
  CapitalStructureDto,
  EquityRefinancingEventDto,
  EquityRefinancingEventMovementDto,
  ProjectDto,
} from '@app/shared/models/contracts/project-dto';
import { useForm } from 'react-hook-form';
import { useDateChangeBeforeValuationModal } from './useDateChangeBeforeValuationModal';
import { CapitalStructureBuildStructureDto } from '@app/shared/models/contracts/capital-structure-debt-instrument-dto';
import { getUpdatedMovements } from '../editEventHelper';
import { calculateBuildStructure } from '@app/core/store/capital-structure-slice';
import { useAppDispatch } from '@app/core/hooks/redux-hooks';

export const useEditEventForm = (
  eventNarrative: string,
  eventDate: string,
  project: ProjectDto,
  capitalStructure: CapitalStructureDto,
  buildStructure: CapitalStructureBuildStructureDto,
  capitalStructureKey: string,
  buildStructureEventId: string
) => {
  const dispatch = useAppDispatch();
  const formMethods = useForm<EquityRefinancingEventDto>({
    ...formConfigBase,
    mode: 'onChange',
    defaultValues: {
      narrative: eventNarrative,
      eventDate: eventDate,
    },
  });

  const { dirtyFields } = formMethods.formState;
  const hasHeaderChanged = Boolean(dirtyFields.eventDate) || Boolean(dirtyFields.narrative);

  const {
    confirmDateChange,
    showDateChangeModal,
    setShowDateChangeModal,
    cancelDateChange,
    displayDateChangeModal,
  } = useDateChangeBeforeValuationModal(
    project,
    capitalStructure,
    buildStructure,
    formMethods.getValues,
    formMethods.reset,
    capitalStructureKey,
    buildStructureEventId
  );

  const dispatchCalculateBuildStructure = (
    newEventDate: string,
    updatedMovements: Record<string, EquityRefinancingEventMovementDto>
  ): void => {
    dispatch(
      calculateBuildStructure({
        project: {
          ...project,
          capitalStructures: {
            ...project.capitalStructures,
            [capitalStructureKey]: {
              ...capitalStructure,
              events: {
                ...capitalStructure.events,
                [buildStructureEventId]: {
                  ...capitalStructure.events[buildStructureEventId],
                  narrative: formMethods.getValues().narrative,
                  eventDate: newEventDate,
                  movements: updatedMovements,
                },
              },
            },
          },
        },
        ignoreErrors: true,
      })
    );
  };

  const formSubmitHandler = () => {
    const newEventDate = formMethods.getValues().eventDate;

    if (displayDateChangeModal(newEventDate)) {
      setShowDateChangeModal(true);
      return;
    }

    const currentMovements = capitalStructure.events[buildStructureEventId]?.movements ?? {};
    const updatedMovements = getUpdatedMovements(newEventDate, currentMovements);

    dispatchCalculateBuildStructure(newEventDate, updatedMovements);
  };

  return {
    formMethods,
    formSubmitHandler,
    cancelDateChange,
    showDateChangeModal,
    confirmDateChange,
    hasHeaderChanged,
  };
};
